/**
 * V3.2: Line chart visualization for pivot reports
 * Best for time-series data (e.g., trends over quarters/months)
 */

import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { PivotReportResult } from '../../types'
import { flattenPivotForChart, getChartSeriesKeys, getSeriesColor, getSeriesLabel } from './chartUtils'

interface PivotLineChartProps {
	pivot: PivotReportResult
	height?: number
}

export function PivotLineChart({ pivot, height = 400 }: PivotLineChartProps) {
	const data = flattenPivotForChart(pivot)
	const seriesKeys = getChartSeriesKeys(pivot)

	if (data.length === 0) {
		return (
			<div className='flex items-center justify-center h-64 text-neutral-500'>
				No data available for chart visualization
			</div>
		)
	}

	// Limit data points for readability
	const maxDataPoints = 30
	const displayData = data.slice(0, maxDataPoints)
	const isTruncated = data.length > maxDataPoints

	return (
		<div className='w-full'>
			<ResponsiveContainer width='100%' height={height}>
				<LineChart data={displayData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
					<CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
					<XAxis dataKey='name' tick={{ fontSize: 12 }} angle={-45} textAnchor='end' interval={0} height={80} />
					<YAxis tick={{ fontSize: 12 }} />
					<Tooltip
						contentStyle={{
							backgroundColor: 'white',
							border: '1px solid #e5e7eb',
							borderRadius: '6px',
							fontSize: '12px',
						}}
						formatter={(value: number | undefined) => (value !== null && value !== undefined ? value.toFixed(2) : '-')}
					/>
					<Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} verticalAlign='top' height={36} />
					{seriesKeys.map((key, index) => (
						<Line
							key={key}
							type='monotone'
							dataKey={key}
							name={getSeriesLabel(key, pivot)}
							stroke={getSeriesColor(index)}
							strokeWidth={2}
							dot={{ fill: getSeriesColor(index), strokeWidth: 2, r: 4 }}
							activeDot={{ r: 6 }}
							connectNulls
						/>
					))}
				</LineChart>
			</ResponsiveContainer>
			{isTruncated && (
				<p className='text-xs text-neutral-500 text-center mt-2'>
					Showing first {maxDataPoints} of {data.length} data points
				</p>
			)}
		</div>
	)
}
